// useFileSectionPages.js
// The visible page of each section of a file list, and the controls that move between
// pages (utils/filePages). A section's position is a stack of the cursors of the pages
// the user has passed (the first page's is null), so Previous is a pop and pages
// recompute live as files change section.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { pageAfter, uploadKey } from '../utils/filePages';

// `sections` is { [sectionId]: files sorted newest first }. Every stack restarts when
// `resetKey` changes (another profile, upload or board). Returns, per section, the page
// { files, hasNext, hasPrevious, pageNumber } and next(id) and previous(id).
export default function useFileSectionPages(sections, resetKey) {
  const [stacks, setStacks] = useState({});
  useEffect(() => { setStacks({}); }, [resetKey]);

  const pages = useMemo(() => Object.fromEntries(Object.entries(sections).map(([id, files]) => {
    const stack = stacks[id] || [null];
    const page = pageAfter(files, stack[stack.length - 1]);
    // No page count: the pages recompute live as files change section, so a total
    // counted now could disagree with the pages the user has walked through.
    return [id, { ...page, pageNumber: stack.length, hasPrevious: stack.length > 1 }];
  })), [sections, stacks]);

  const previous = useCallback((id) => setStacks((prev) => {
    const stack = prev[id] || [null];
    return stack.length > 1 ? { ...prev, [id]: stack.slice(0, -1) } : prev;
  }), []);

  const next = useCallback((id) => {
    const page = pages[id];
    if (!page?.hasNext) return;
    const cursor = uploadKey(page.files[page.files.length - 1]);
    setStacks((prev) => ({ ...prev, [id]: [...(prev[id] || [null]), cursor] }));
  }, [pages]);

  // A page emptied by files leaving its section (not the first page) steps back.
  useEffect(() => {
    Object.entries(pages).forEach(([id, page]) => {
      if (page.files.length === 0 && page.hasPrevious) previous(id);
    });
  }, [pages, previous]);

  return { pages, next, previous };
}
